import type { Types } from '../../types';
import type { ConnectedTypes } from '../../make-connected';
import { getVertexId } from '../../../core/util';
import { idOf } from './util/util';


export function fromLinkedEventsSets (
  _linkedEventsSets: ConnectedTypes.linkedEventsSet[],
  _settings:         ConnectedTypes.divisionSettings,
  presentEventIds:   Set<string>,
  options:           Types.parsedToOptions
): Types.Entities.LinkedEventsSet[] {

  return (_linkedEventsSets ?? [])
    .map(x => {
      // ensure that linkedEventsSets conform to the expected union that is currently not enforced by the schema
      if (x.type === 'parallel') {
        if (x.anchor == null) throw new Error(`Linked events set ${getVertexId(x, options)} of type "parallel" is missing "anchor" property`);
        return { ...x, type: 'parallel', anchor: x.anchor } as const;
      }
      else if (x.type === 'sequential') {
        if (x.gap == null) throw new Error(`Linked events set ${getVertexId(x, options)} of type "sequential" is missing "gap" property`);
        return { ...x, type: 'sequential', gap: x.gap } as const;
      }
      else if (x.type === 'custom') {
        return { ...x, type: 'custom' } as const;
      }

      throw new Error(`Linked events set ${getVertexId(x, options)} is missing "type" property`);
    })
    .map((x): Types.Entities.LinkedEventsSet => {
      type Configurations = Types.Entities.LinkedEventsSet['configurations'];
      const id = getVertexId(x, options);

      const restrictedEvents = x.events.filter(x => presentEventIds.has(idOf.event(x, options)));
      const type = x.type;
      if (type === 'parallel') {
        const configurations: Configurations = [];
        const anchor = x.anchor;
        switch (anchor) {
          case 'start':  configurations.push(restrictedEvents.map(() => ({ anchor: 'start',  offset: 0 }))); break;
          case 'end':    configurations.push(restrictedEvents.map(() => ({ anchor: 'end',    offset: 0 }))); break;
          case 'middle': configurations.push(restrictedEvents.map(() => ({ anchor: 'middle', offset: 0 }))); break;
          default: throw new Error(`Unrecognized anchor "${anchor satisfies never as string}" in linked events set ${getVertexId(x, options)}`);
        }
        return {
          id, configurations,
          events: restrictedEvents.map(x => idOf.event(x, options))
        };
      }

      if (type === 'sequential') {
        const { min, max } = x.gap;
        const dt = _settings.discretization;

        const numConfigurations = Math.pow(1 + Math.round(max/dt - min/dt), restrictedEvents.length - 1);
        const maxNumConfigurations = 49; // max 3 events and a maximum gap range of 30 minutes => 7^2 = 49
        if (numConfigurations >= maxNumConfigurations) throw new Error(`Linked events set ${getVertexId(x, options)} has too many configurations (${numConfigurations}, max allowed is ${maxNumConfigurations})`);

        const gaps: number[] = [];
        for (let i = min; i <= max; i += dt) gaps.push(i);

        let configurations: Configurations = [[{ anchor: 'start', offset: 0 }]]; // first event is always at offset 0
        for (let i = 1; i < restrictedEvents.length; i++) {
          const newConfigurations: Configurations = [];
          for (const config of configurations) {
            const lastOffsetBottom = config[config.length - 1].offset + restrictedEvents[i - 1].preferredDuration;
            for (const gap of gaps) {
              newConfigurations.push([...config, { anchor: 'start', offset: lastOffsetBottom + gap }]);
            }
          }
          configurations = newConfigurations;
        }

        return {
          id, configurations,
          events: restrictedEvents.map(x => idOf.event(x, options))
        };
      }

      if (type === 'custom') {
        throw new Error(`Custom linked events sets are not yet supported (linked events set ${getVertexId(x, options)})`);
      }

      throw new Error(`Unrecognized linked events set type "${type satisfies never as string}" in linked events set ${getVertexId(x, options)}`);
    })
    .filter(x => x.events.length > 1); // filter out linked events sets that only contain one event after filtering out events that are not present
}